import * as React from "react";

interface BodyScrollControllerConsumerInterface {
  acquireBodyScrollLock: (lockId: string) => void;
  releaseBodyScrollLock: (lockId: string) => void;
}
const defaultValue = {} as unknown as BodyScrollControllerConsumerInterface;
const BodyScrollControllerContext = React.createContext(defaultValue);

interface BodyScrollControllerProps {
  guid: string;
}
interface BodyScrollLock {
  lockId: string;
}
export const BodyScrollControllerProvider: React.FunctionComponent<
  BodyScrollControllerProps
> = (props) => {
  const thisEventNameBase = `body-scroll-controller-${props.guid}`;

  const [bodyScrollLocks, setBodyScrollLocks] = React.useState<
    BodyScrollLock[]
  >([]);

  const addBodyScrollLock = React.useCallback(() => {
    document.body.classList.add("bbnt-modal-visible-scroll-that-only");
  }, []);
  const removeBodyScrollLock = React.useCallback(() => {
    document.body.classList.remove("bbnt-modal-visible-scroll-that-only");
  }, []);

  /**
   * Event listeners
   */
  React.useEffect(() => {
    window.addEventListener(
      `${thisEventNameBase}-add-body-scroll-lock`,
      addBodyScrollLock
    );
    return () => {
      window.removeEventListener(
        `${thisEventNameBase}-add-body-scroll-lock`,
        addBodyScrollLock
      );
    };
  }, [addBodyScrollLock]);
  React.useEffect(() => {
    window.addEventListener(
      `${thisEventNameBase}-remove-body-scroll-lock`,
      removeBodyScrollLock
    );
    return () => {
      window.removeEventListener(
        `${thisEventNameBase}-remove-body-scroll-lock`,
        removeBodyScrollLock
      );
    };
  }, [removeBodyScrollLock]);

  /**
   * Event triggers
   */
  React.useEffect(() => {
    if (bodyScrollLocks.length > 0) {
      window.dispatchEvent(
        new Event(`${thisEventNameBase}-add-body-scroll-lock`)
      );
    } else {
      window.dispatchEvent(
        new Event(`${thisEventNameBase}-remove-body-scroll-lock`)
      );
    }
  }, [bodyScrollLocks]);

  /**
   * Consumer
   */

  const acquireBodyScrollLock: BodyScrollControllerConsumerInterface["acquireBodyScrollLock"] =
    React.useCallback(
      (lockId) => {
        const newBodyScrollLock: BodyScrollLock = {
          lockId,
        };
        const bodyScrollLocksWithThisNewLock = [
          ...bodyScrollLocks,
          ...[newBodyScrollLock],
        ];
        setBodyScrollLocks(bodyScrollLocksWithThisNewLock);
      },
      [bodyScrollLocks, setBodyScrollLocks]
    );
  const releaseBodyScrollLock: BodyScrollControllerConsumerInterface["releaseBodyScrollLock"] =
    React.useCallback(
      (lockId) => {
        const bodyScrollLocksWithoutThisLock = bodyScrollLocks.filter(
          (bodyScrollLock) => bodyScrollLock.lockId !== lockId
        );
        setBodyScrollLocks(bodyScrollLocksWithoutThisLock);
      },
      [bodyScrollLocks, setBodyScrollLocks]
    );
  const value: BodyScrollControllerConsumerInterface = React.useMemo(
    () => ({
      acquireBodyScrollLock,
      releaseBodyScrollLock,
    }),
    [acquireBodyScrollLock, releaseBodyScrollLock]
  );
  return (
    <BodyScrollControllerContext.Provider value={value}>
      {props.children || []}
    </BodyScrollControllerContext.Provider>
  );
};

export const useBodyScrollController = () => {
  const value = React.useContext(BodyScrollControllerContext);
  if (value === defaultValue) {
    throw new Error(
      "useBodyScrollController must be used within a BodyScrollControllerProvider"
    );
  }
  return value;
};
